import { GetServerSidePropsContext } from 'next'
import { useTranslation, Trans } from 'next-i18next'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import Meta from '@/components/Meta'
import Results from '@/components/parkings/Results'
import HeroCity from '@/components/parkings/HeroCity/HeroCity'
import SeoDescription from '@/components/parking/SeoDescription'
import HaveParking from '@/components/sections/HaveParking'
import ContactSection from '@/components/sections/ContactSection'
import Partners from '@/components/sections/Partners'
import CustomReveal from '@/components/common/CustomReveal'
import { loadTranslationsWithMarket } from '@/utils/loadTranslations'
import DefaultLayout from '@/components/layouts/Default'
import { useFetchByCity } from '@/service/apiHooks/useFetchByCity'
import { useFetchAllCities } from '@/service/apiHooks/useFetchAllCities'
import NoResults from '@/components/parkings/NoResults'
import Spinner from '@/components/common/SpinnerPage'
import PopupBottomView from '@/components/Popups/PopupBottomView'
import { useMarket } from '@/utils/multi-markets/context'

const MapView = dynamic(() => import('@/components/parkings/MapView'), {
  ssr: false
})

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  return loadTranslationsWithMarket(context, {
    cache: 'stale-while-revalidate',
    namespaces: ['common', 'parkings', 'popups', 'faq']
  })
}

function ParkingsSearch() {
  const { t } = useTranslation('parkings')
  const { query, locale } = useRouter()
  const { market } = useMarket()

  const {
    parkings,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useFetchByCity({
    slug: query.city as string,
    params: { lang: locale, size: 10, page: 0 }
  })

  const { cities, isLoading: isLoadingCities } = useFetchAllCities({
    params: { lang: locale }
  })

  const removeDiacritics = (str: string) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }

  const city = cities?.items?.find(
    city =>
      city?.cityUrlSlug &&
      removeDiacritics(city.cityUrlSlug) ===
        removeDiacritics(query.city as string)
  )

  const metaString = t('meta.bookCitySpace', { city: query?.city ?? '' })

  return (
    <DefaultLayout>
      <div id="popup-wrapper" />

      {city?.cityUrlSlugs && city?.cityUrlSlugs.en && (
        <Meta
          title={metaString}
          description={t(`meta.${city?.cityUrlSlugs.en}.description` as any)}
        />
      )}
      <HeroCity
        text={
          <Trans
            t={t}
            i18nKey="hero.cityTitle"
            values={{ city: city?.label }}
          />
        }
      />

      <CustomReveal>
        {isLoading || isLoadingCities ? (
          <Spinner />
        ) : (
          <>
            {parkings && city ? (
              <div id="parkings-section" data-test-id="parkings-section">
                <Results
                  parkings={parkings}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage || false}
                  isFetchingNextPage={isFetchingNextPage}
                />
                <MapView parkings={parkings} city={city} />
              </div>
            ) : (
              <NoResults />
            )}
          </>
        )}
      </CustomReveal>

      {t(`seoDescription.${city?.cityUrlSlugs?.en}` as any).length > 50 ? (
        <SeoDescription name={city?.cityUrlSlugs?.en as any} />
      ) : (
        <></>
      )}
      <ContactSection />
      <Partners />
      <HaveParking />
      {market !== 'bh' && <PopupBottomView />}
    </DefaultLayout>
  )
}

export default ParkingsSearch
